import React, { useState, useEffect, useRef, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Eye, EyeOff, ArrowLeft, User, Search, X, Check, Plus,
  Globe, Mail, Phone, MapPin, Video, Calendar,
  Briefcase, FileText, Paperclip, MessageSquare,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../constants/api';
import ContactLogo from '../components/shared/ContactLogo';
import { getRecaptchaToken } from '../lib/recaptcha';

/* ─── Google icon (brand colors, not in lucide) ─────────────── */
const IconGoogle = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

/* Reactivado (oct 2026) — el cliente OAuth se había eliminado en Google
   Cloud Console; con uno nuevo configurado vuelve a funcionar. */
const SHOW_GOOGLE_AUTH = true;

/* ─── Brand icons — not part of this lucide-react build, reuses the
   same paths already drawn in ProfilePreview.js's getIcon(), each
   rendered in its real brand color (not a flat accent silhouette). ── */
const brandPaths = {
  linkedin: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  facebook: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
  whatsapp: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z',
  tiktok: 'M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z',
  youtube: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
  github: 'M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12',
  twitter: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
};
const BRAND_COLORS = {
  linkedin: '#0A66C2', facebook: '#1877F2', whatsapp: '#25D366',
  youtube: '#FF0000', github: '#181717', twitter: '#000000', tiktok: '#000000',
};
const IconBrand = ({ type, size = 16 }) => {
  const gradId = useId();
  if (type === 'instagram') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24">
        <defs>
          <linearGradient id={gradId} x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FFDC80" />
            <stop offset="25%" stopColor="#FCAF45" />
            <stop offset="50%" stopColor="#E1306C" />
            <stop offset="75%" stopColor="#C13584" />
            <stop offset="100%" stopColor="#5851DB" />
          </linearGradient>
        </defs>
        <path fill={`url(#${gradId})`} d={brandPaths.instagram} />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={BRAND_COLORS[type] || 'currentColor'}>
      <path d={brandPaths[type]} />
    </svg>
  );
};
const Linkedin = (p) => <IconBrand type="linkedin" {...p} />;
const Instagram = (p) => <IconBrand type="instagram" {...p} />;
const Facebook = (p) => <IconBrand type="facebook" {...p} />;
const Youtube = (p) => <IconBrand type="youtube" {...p} />;
const Github = (p) => <IconBrand type="github" {...p} />;
const Twitter = (p) => <IconBrand type="twitter" {...p} />;
const Whatsapp = (p) => <IconBrand type="whatsapp" {...p} />;
const Tiktok = (p) => <IconBrand type="tiktok" {...p} />;

const hexToRgba = (hex, alpha) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

/* ─── Icon badge — brand items get a tinted circle in their real
   color; generic (non-brand) items fall back to the app accent. ──── */
const LinkBadge = ({ opt, size = 30 }) => {
  const tint = opt.brandColor ? hexToRgba(opt.brandColor, 0.12) : 'var(--ob-accent-soft)';
  const color = opt.brandColor || 'var(--ob-accent)';
  return (
    <span className="ob-link-badge" style={{ width: size, height: size, borderRadius: Math.round(size * 0.3), background: tint, color }}>
      <opt.icon size={Math.round(size * 0.53)} />
    </span>
  );
};

/* ─── Link catalogue — dedicated ProfileUpdate fields where they
   exist, otherwise flagged `custom` to go into one of the 4 generic
   custom_link slots. `field` values ending in a dot path are read via
   getLinkValue/setLinkValue below. `brandColor` drives LinkBadge's
   tint for real brand icons; generic types omit it. ────────────── */
const LINK_OPTIONS = [
  { key: 'texto', label: 'Texto', icon: MessageSquare, categories: ['recomendados'], custom: true },
  { key: 'email', label: 'Email', icon: Mail, categories: ['recomendados', 'contacto'], field: 'contactEmail' },
  { key: 'instagram', label: 'Instagram', icon: Instagram, categories: ['recomendados', 'redes'], field: 'links.instagram', brandColor: '#E1306C' },
  { key: 'website', label: 'Sitio web', icon: Globe, categories: ['recomendados', 'negocio'], field: 'links.website' },
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, categories: ['recomendados', 'redes'], field: 'links.linkedin', brandColor: '#0A66C2' },
  { key: 'llamar', label: 'Llamar', icon: Phone, categories: ['recomendados', 'contacto'], field: 'phone' },
  { key: 'whatsapp', label: 'WhatsApp', icon: Whatsapp, categories: ['recomendados', 'contacto'], field: 'links.whatsapp', brandColor: '#25D366' },
  { key: 'direccion', label: 'Dirección', icon: MapPin, categories: ['contacto'], custom: true },
  { key: 'facetime', label: 'FaceTime', icon: Video, categories: ['contacto'], custom: true },
  { key: 'calendly', label: 'Calendly', icon: Calendar, categories: ['contacto'], custom: true },
  { key: 'tiktok', label: 'TikTok', icon: Tiktok, categories: ['redes'], field: 'links.tiktok', brandColor: '#000000' },
  { key: 'youtube', label: 'YouTube', icon: Youtube, categories: ['redes'], custom: true, brandColor: '#FF0000' },
  { key: 'facebook', label: 'Facebook', icon: Facebook, categories: ['redes'], field: 'links.facebook', brandColor: '#1877F2' },
  { key: 'twitter', label: 'X / Twitter', icon: Twitter, categories: ['redes'], custom: true, brandColor: '#000000' },
  { key: 'portfolio', label: 'Portfolio', icon: Briefcase, categories: ['negocio'], custom: true },
  { key: 'github', label: 'GitHub', icon: Github, categories: ['negocio'], custom: true, brandColor: '#181717' },
  { key: 'behance', label: 'Behance', icon: FileText, categories: ['negocio'], custom: true },
  { key: 'notion', label: 'Notion', icon: FileText, categories: ['negocio'], custom: true },
  { key: 'pdf', label: 'PDF / documento', icon: Paperclip, categories: ['negocio'], custom: true },
];
const findOpt = (key) => LINK_OPTIONS.find((o) => o.key === key);
const RECOMMENDED_KEYS = ['linkedin', 'website', 'instagram', 'whatsapp', 'email', 'llamar'];
const MODAL_CATEGORIES = [
  { key: 'recomendados', label: 'Recomendados' },
  { key: 'contacto', label: 'Contacto' },
  { key: 'redes', label: 'Redes sociales' },
  { key: 'negocio', label: 'Negocio / contenido' },
];

const TOTAL_STEPS = 5;

const getInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

/* ─── Live preview — phone/card mockup, mirrors the profile being
   built. Intentionally a small bespoke mockup (not the real
   ProfilePreview component) so it stays fast, self-contained and has
   no dependency on a fully-formed profile/template/colors object. ── */
const LivePreviewCard = ({ name, position, company, photoPreviewUrl, contactEmail, phone, links, customLinks }) => {
  const rows = [];
  if (contactEmail) rows.push({ key: 'email', icon: Mail, label: contactEmail, color: null });
  if (phone) rows.push({ key: 'phone', icon: Phone, label: phone, color: null });
  if (links.linkedin) rows.push({ key: 'linkedin', icon: Linkedin, label: 'LinkedIn', color: BRAND_COLORS.linkedin });
  if (links.instagram) rows.push({ key: 'instagram', icon: Instagram, label: 'Instagram', color: '#E1306C' });
  if (links.website) rows.push({ key: 'website', icon: Globe, label: 'Sitio web', color: null });
  if (links.whatsapp) rows.push({ key: 'whatsapp', icon: Whatsapp, label: 'WhatsApp', color: BRAND_COLORS.whatsapp });
  if (links.tiktok) rows.push({ key: 'tiktok', icon: Tiktok, label: 'TikTok', color: BRAND_COLORS.tiktok });
  if (links.facebook) rows.push({ key: 'facebook', icon: Facebook, label: 'Facebook', color: BRAND_COLORS.facebook });
  customLinks.forEach((l) => {
    if (!l.value) return;
    const opt = findOpt(l.key);
    rows.push({ key: l.key || l.title, icon: opt?.icon || FileText, label: l.title, color: opt?.brandColor || null });
  });

  return (
    <div className="ob-phone-mock">
      <div className="ob-phone-banner">
        <div className="ob-phone-avatar">
          {photoPreviewUrl ? <img src={photoPreviewUrl} alt="" /> : <span>{getInitials(name) || <User size={26} />}</span>}
        </div>
      </div>
      <div className="ob-phone-body">
        <div className="ob-phone-name">{name || 'Tu nombre'}</div>
        {(position || company) && (
          <div className="ob-phone-role">{[position, company].filter(Boolean).join(' · ')}</div>
        )}
        <button type="button" className="ob-phone-save-btn"><Phone size={14} /> Guardar contacto</button>
        {rows.length > 0 && (
          <div className="ob-phone-links">
            {rows.map((r) => (
              <div className="ob-phone-link-row" key={r.key}>
                <span style={r.color ? { color: r.color } : undefined}><r.icon size={14} /></span>
                <span>{r.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Modal: add extra links ─────────────────────────────────── */
const LinksModal = ({ onClose, onPick, search, setSearch, category, setCategory, isFilled }) => {
  const filtered = LINK_OPTIONS.filter((o) => {
    const matchesCategory = category === 'recomendados' ? o.categories.includes('recomendados') : o.categories.includes(category);
    const matchesSearch = !search.trim() || o.label.toLowerCase().includes(search.trim().toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return (
    <div className="ob-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ob-links-modal">
        <button className="ob-links-modal-close" onClick={onClose} aria-label="Cerrar"><X size={16} /></button>
        <div className="ob-links-modal-head">
          <h2 className="ob-links-modal-title">Agregar contenido</h2>
          <p className="ob-links-modal-sub">Selecciona enlaces e información para tu perfil.</p>
        </div>
        <div className="ob-links-search">
          <Search size={16} />
          <input placeholder="Buscar contenido..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="ob-links-chips">
          {MODAL_CATEGORIES.map((c) => (
            <button
              key={c.key}
              className={`ob-links-chip ${category === c.key ? 'ob-links-chip--active' : ''}`}
              onClick={() => setCategory(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="ob-links-grid">
          {filtered.map((opt) => {
            const filled = isFilled(opt);
            return (
              <button key={opt.key} className="ob-links-grid-item" onClick={() => onPick(opt)}>
                <LinkBadge opt={opt} size={30} />
                <span className="ob-links-grid-label">{opt.label}</span>
                <span className={`ob-links-grid-add ${filled ? 'ob-links-grid-add--filled' : ''}`}>
                  {filled ? <Check size={14} /> : <Plus size={14} />}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && <div className="ob-links-empty">Sin resultados para "{search}".</div>}
        </div>
      </div>
    </div>
  );
};

const RegisterOnboardingPage = () => {
  const navigate = useNavigate();
  const { register, user, updateUser } = useAuth();

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [contentPhase, setContentPhase] = useState('resting');
  const [transitioning, setTransitioning] = useState(false);

  const [fullName, setFullName] = useState('');
  const [position, setPosition] = useState('');
  const [company, setCompany] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [links, setLinks] = useState({ linkedin: '', website: '', instagram: '', whatsapp: '', tiktok: '', facebook: '' });
  const [customLinks, setCustomLinks] = useState([]); // [{key, title, value}] — up to 4, mapped to custom_link_1..4
  const [expandedLinks, setExpandedLinks] = useState({}); // which recommended cards show their inline input

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');

  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  const [fieldError, setFieldError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resuming, setResuming] = useState(false);

  const [linksModalOpen, setLinksModalOpen] = useState(false);
  const [linksModalSearch, setLinksModalSearch] = useState('');
  const [linksModalCategory, setLinksModalCategory] = useState('recomendados');

  const fileInputRef = useRef(null);
  const resumeAttempted = useRef(false);

  // Admin kill-switch — checked once on mount. Defaults to enabled so a
  // slow/failed request never traps a real visitor behind a false
  // "restricted" screen; backend still rejects the actual account creation
  // regardless of what this shows.
  const [registrationEnabled, setRegistrationEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings/registration-status`)
      .then(({ data }) => { if (!cancelled) setRegistrationEnabled(data.enabled !== false); })
      .catch(() => { if (!cancelled) setRegistrationEnabled(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!accountEmail && contactEmail) setAccountEmail(contactEmail);
    // eslint-disable-next-line
  }, [contactEmail]);

  /* ─── Resume after a Google sign-up started at Step 5 ───── */
  useEffect(() => {
    if (resumeAttempted.current) return;
    const draftRaw = sessionStorage.getItem('cn_onboarding_draft');
    if (!draftRaw || !user) return;
    resumeAttempted.current = true;
    sessionStorage.removeItem('cn_onboarding_draft');
    let draft = {};
    try {
      draft = JSON.parse(draftRaw);
      setPosition(draft.position || '');
      setCompany(draft.company || '');
      setContactEmail(draft.contactEmail || '');
      setPhone(draft.phone || '');
      setLinks(draft.links || { linkedin: '', website: '', instagram: '', whatsapp: '', tiktok: '', facebook: '' });
      setCustomLinks(draft.customLinks || []);
      setFullName(user.name || '');
      setAccountEmail(user.email || '');
    } catch (e) { /* ignore malformed draft */ }
    setResuming(true);
    finalizeAccount({ name: user.name, email: user.email, viaGoogle: true, draft });
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    return () => { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); };
  }, [photoPreviewUrl]);

  /* ─── Step transition ────────────────────────────────────── */
  const changeStep = (newStep, dir) => {
    if (transitioning) return;
    setTransitioning(true);
    setDirection(dir);
    setFieldError('');
    setContentPhase(dir === 1 ? 'out-fwd' : 'out-back');
    setTimeout(() => {
      setStep(newStep);
      setContentPhase(dir === 1 ? 'enter-fwd' : 'enter-back');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setContentPhase('resting'));
      });
      setTimeout(() => setTransitioning(false), 210);
    }, 110);
  };

  const handleBack = () => {
    if (transitioning) return;
    if (step === 1) { navigate('/login'); return; }
    changeStep(step - 1, -1);
  };

  /* ─── Validation per step ────────────────────────────────── */
  const validateStep = () => {
    if (step === 1) {
      if (!fullName.trim()) return 'Ingresa tu nombre.';
    }
    if (step === 5) {
      if (!accountEmail.trim()) return 'Ingresa tu correo.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountEmail.trim())) return 'Ingresa un correo válido.';
      // Matches the backend's validate_password_strength() (also used at
      // registration and password reset) — this used to accept 6
      // characters with no other requirement, weaker than both of those,
      // so a password accepted here could still be rejected by the
      // backend with no client-side warning beforehand.
      if (password.length < 8) return 'Mínimo 8 caracteres.';
      if (!/[A-Z]/.test(password)) return 'Incluye al menos una letra mayúscula.';
      if (!/[a-z]/.test(password)) return 'Incluye al menos una letra minúscula.';
      if (!/[0-9]/.test(password)) return 'Incluye al menos un número.';
    }
    return '';
  };

  const handlePrimary = async () => {
    if (transitioning || submitting) return;
    const err = validateStep();
    if (err) { setFieldError(err); return; }
    if (step < TOTAL_STEPS) {
      changeStep(step + 1, 1);
      return;
    }
    await finalizeAccount({ name: fullName, email: accountEmail, password, viaGoogle: false });
  };

  /* ─── Photo picking ──────────────────────────────────────── */
  const handlePhotoPick = () => fileInputRef.current?.click();
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setFieldError('Selecciona un archivo de imagen.'); return; }
    if (file.size > 5 * 1024 * 1024) { setFieldError('La imagen no puede superar 5MB.'); return; }
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoFile(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    setFieldError('');
  };

  /* ─── Links: recommended row + modal ─────────────────────── */
  const getLinkValue = (opt) => {
    if (!opt.field) return '';
    if (opt.field === 'contactEmail') return contactEmail;
    if (opt.field === 'phone') return phone;
    if (opt.field.startsWith('links.')) return links[opt.field.split('.')[1]] || '';
    return '';
  };
  const setLinkValue = (opt, value) => {
    if (!opt.field) return;
    if (opt.field === 'contactEmail') { setContactEmail(value); return; }
    if (opt.field === 'phone') { setPhone(value); return; }
    if (opt.field.startsWith('links.')) {
      const k = opt.field.split('.')[1];
      setLinks((prev) => ({ ...prev, [k]: value }));
    }
  };

  const toggleExpanded = (key) => setExpandedLinks((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleModalPick = (opt) => {
    setLinksModalOpen(false);
    if (opt.custom) {
      setCustomLinks((prev) => {
        if (prev.some((l) => l.key === opt.key)) return prev; // already added
        if (prev.length >= 4) return prev; // only 4 custom slots exist on the backend
        return [...prev, { key: opt.key, title: opt.label, value: '' }];
      });
      return;
    }
    setExpandedLinks((prev) => ({ ...prev, [opt.key]: true }));
  };

  const removeCustomLink = (key) => setCustomLinks((prev) => prev.filter((l) => l.key !== key));

  /* ─── Google — prominent option at Step 5 ────────────────── */
  const handleGoogleAtEmailStep = () => {
    sessionStorage.setItem('cn_onboarding_draft', JSON.stringify({ position, company, contactEmail, phone, links, customLinks }));
    window.location.href = `${API}/auth/google/login`;
  };

  /* ─── Finalize: create account, build profile, mark done ── */
  const finalizeAccount = async ({ name, email: finalEmail, password: finalPassword, viaGoogle, draft }) => {
    setSubmitting(true);
    setFieldError('');
    sessionStorage.removeItem('cn_onboarding_draft');
    try {
      if (!viaGoogle) {
        const recaptchaToken = await getRecaptchaToken('register');
        const result = await register(name, finalEmail, finalPassword, recaptchaToken);
        if (!result.success) {
          setFieldError(result.error || 'No pudimos crear tu cuenta. Intenta nuevamente.');
          setSubmitting(false);
          setResuming(false);
          return;
        }
      }

      const d = viaGoogle ? (draft || {}) : { position, company, contactEmail, phone, links, customLinks };

      const createRes = await axios.post(`${API}/profiles/create`);
      const profile = createRes.data;

      const trimmedName = (name || '').trim();
      const parts = trimmedName.split(/\s+/).filter(Boolean);
      const first = parts[0] || trimmedName;
      const rest = parts.slice(1).join(' ');

      const dLinks = d.links || {};
      const dCustom = d.customLinks || [];
      const updatePayload = {
        name: first,
        lastname: rest,
        phone: (d.phone || '').trim() || null,
        whatsapp: (d.phone || '').trim() || null,
        position: (d.position || '').trim() || null,
        company: (d.company || '').trim() || null,
        email: (d.contactEmail || finalEmail || '').trim() || null,
        linkedin: dLinks.linkedin?.trim() || null,
        website: dLinks.website?.trim() || null,
        instagram: dLinks.instagram?.trim() || null,
        tiktok: dLinks.tiktok?.trim() || null,
        facebook: dLinks.facebook?.trim() || null,
      };
      dCustom.slice(0, 4).forEach((l, i) => {
        if (l.value?.trim()) {
          updatePayload[`custom_link_${i + 1}`] = l.value.trim();
          updatePayload[`custom_link_title_${i + 1}`] = l.title;
        }
      });
      // whatsapp field from the links picker (distinct from the phone/"Llamar" field) wins if set
      if (dLinks.whatsapp?.trim()) updatePayload.whatsapp = dLinks.whatsapp.trim();

      const updateRes = await axios.put(`${API}/profile/${profile.id}`, updatePayload);

      if (!viaGoogle && photoFile) {
        try {
          const fd = new FormData();
          fd.append('file', photoFile);
          await axios.post(`${API}/upload/profile-image/${profile.id}`, fd);
        } catch (e) { /* photo is optional — don't block account creation */ }
      }

      try {
        await axios.put(`${API}/users/onboarding-status`, { onboarding_completed: true });
      } catch (e) { /* non-critical */ }

      // This wizard already walks the user through building their profile,
      // so skip the separate dashboard product tour that would otherwise
      // auto-trigger for a brand-new account.
      try {
        await axios.put(`${API}/users/tutorial-status`, { tutorial_completed: true });
      } catch (e) { /* non-critical */ }
      updateUser({ onboarding_completed: true, tutorial_completed: true });

      // The "choose your plan" popup shows once the user is inside the
      // dashboard, not here — see Dashboard's effect in App.js.
      sessionStorage.setItem('cn_show_plans_modal', '1');
      navigate('/dashboard');
    } catch (e) {
      setFieldError('Ocurrió un error al crear tu cuenta. Intenta nuevamente.');
    } finally {
      setSubmitting(false);
      setResuming(false);
    }
  };

  /* ─── Step content ───────────────────────────────────────── */
  const primaryLabel = () => {
    if (step === 4) return photoFile ? 'Continuar' : 'Omitir por ahora';
    if (step === TOTAL_STEPS) return 'Crear cuenta';
    return 'Continuar';
  };

  const renderRecommendedRow = (key) => {
    const opt = LINK_OPTIONS.find((o) => o.key === key);
    const value = getLinkValue(opt);
    const isOpen = expandedLinks[key] || !!value;
    return (
      <div className="ob-link-row" key={key}>
        <button type="button" className="ob-link-card" onClick={() => toggleExpanded(key)}>
          <LinkBadge opt={opt} />
          <span className="ob-link-card-label">{opt.label}</span>
          <span className={`ob-link-card-add ${value ? 'ob-link-card-add--filled' : ''}`}>
            {value ? <Check size={14} /> : <Plus size={14} />}
          </span>
        </button>
        {isOpen && (
          <input
            className="ob-input ob-link-input"
            placeholder={opt.label}
            value={value}
            onChange={(e) => setLinkValue(opt, e.target.value)}
            autoFocus={expandedLinks[key] && !value}
          />
        )}
      </div>
    );
  };

  const renderStep = () => {
    if (step === 1) {
      return (
        <>
          <h1 className="ob-title">Comencemos</h1>
          <p className="ob-sub">Primero, dinos cómo te llamas.</p>
          <div className="ob-field">
            <label className="ob-label" htmlFor="ob-name">Nombre</label>
            <input
              id="ob-name" className="ob-input" type="text" autoComplete="name"
              placeholder="Nombre completo" value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePrimary()}
              autoFocus
            />
          </div>
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <h1 className="ob-title">¿Dónde trabajas?</h1>
          <p className="ob-sub">Agrega tu cargo y empresa para completar tu tarjeta digital.</p>
          <div className="ob-field">
            <label className="ob-label" htmlFor="ob-position">Cargo</label>
            <input
              id="ob-position" className="ob-input" type="text"
              placeholder="Ej: CEO, Sales Manager, Founder" value={position}
              onChange={(e) => setPosition(e.target.value)}
              autoFocus
            />
          </div>
          <div className="ob-field">
            <label className="ob-label" htmlFor="ob-company">Empresa</label>
            <input
              id="ob-company" className="ob-input" type="text"
              placeholder="Nombre de empresa" value={company}
              onChange={(e) => setCompany(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePrimary()}
            />
          </div>
        </>
      );
    }
    if (step === 3) {
      return (
        <>
          <h1 className="ob-title">Información adicional</h1>
          <p className="ob-sub">Agrega datos de contacto y enlaces para que puedan encontrarte fácilmente.</p>
          <div className="ob-field">
            <label className="ob-label" htmlFor="ob-cemail">Email</label>
            <input
              id="ob-cemail" className="ob-input" type="email"
              placeholder="correo@ejemplo.com" value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </div>
          <div className="ob-field">
            <label className="ob-label" htmlFor="ob-cphone">Teléfono</label>
            <input
              id="ob-cphone" className="ob-input" type="tel"
              placeholder="+56 9 XXXX XXXX" value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="ob-links-section">
            <div className="ob-links-section-title">Links recomendados</div>
            {RECOMMENDED_KEYS.map(renderRecommendedRow)}
          </div>

          {customLinks.length > 0 && (
            <div className="ob-links-section">
              <div className="ob-links-section-title">Otros enlaces</div>
              {customLinks.map((l) => {
                const opt = findOpt(l.key) || { icon: FileText, label: l.title };
                return (
                  <div className="ob-link-row" key={l.key}>
                    <div className="ob-custom-link-head">
                      <LinkBadge opt={opt} size={26} />
                      <span className="ob-custom-link-title">{l.title}</span>
                      <button type="button" className="ob-custom-link-remove" onClick={() => removeCustomLink(l.key)} aria-label="Quitar">
                        <X size={13} />
                      </button>
                    </div>
                    <input
                      className="ob-input ob-link-input"
                      placeholder={l.title}
                      value={l.value}
                      onChange={(e) => setCustomLinks((prev) => prev.map((x) => (x.key === l.key ? { ...x, value: e.target.value } : x)))}
                    />
                  </div>
                );
              })}
            </div>
          )}

          <button type="button" className="ob-add-links-btn" onClick={() => setLinksModalOpen(true)}>
            + Agregar links adicionales
          </button>
        </>
      );
    }
    if (step === 4) {
      return (
        <>
          <h1 className="ob-title">Agrega una foto</h1>
          <p className="ob-sub">Una foto hace que tu perfil se sienta más cercano y confiable.</p>
          <div className="ob-photo-wrap">
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
            <div className="ob-photo-circle" onClick={handlePhotoPick}>
              {photoPreviewUrl
                ? <img src={photoPreviewUrl} alt="Tu foto de perfil" className="ob-photo-img" />
                : <span className="ob-photo-initials">{getInitials(fullName) || <User size={30} />}</span>}
            </div>
            <button type="button" className="ob-btn-outline" onClick={handlePhotoPick}>
              {photoFile ? 'Cambiar foto' : 'Agregar foto'}
            </button>
          </div>
        </>
      );
    }
    // step 5
    return (
      <>
        <h1 className="ob-title">Guarda tu tarjeta</h1>
        <p className="ob-sub">Crea tu cuenta para guardar tu perfil digital y empezar a compartirlo.</p>

        {SHOW_GOOGLE_AUTH && (
          <>
            <button type="button" className="ob-btn-google" onClick={handleGoogleAtEmailStep} disabled={submitting}>
              <IconGoogle /> Continuar con Google
            </button>
            <div className="ob-divider"><span>o</span></div>
          </>
        )}

        <div className="ob-field">
          <label className="ob-label" htmlFor="ob-email">Email</label>
          <input
            id="ob-email" className="ob-input" type="email" autoComplete="email"
            placeholder="correo@ejemplo.com" value={accountEmail}
            onChange={(e) => setAccountEmail(e.target.value)}
          />
        </div>
        <div className="ob-field">
          <label className="ob-label" htmlFor="ob-password">Contraseña</label>
          <div className="ob-pw-wrap">
            <input
              id="ob-password" className="ob-input ob-input--pw"
              type={showPw ? 'text' : 'password'} autoComplete="new-password"
              placeholder="Mínimo 8 caracteres" value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePrimary()}
            />
            <button
              type="button" className="ob-pw-toggle" onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <p className="ob-legal">Al continuar, aceptas nuestros Términos y Privacidad.</p>
      </>
    );
  };

  if (resuming) {
    return (
      <div className="ob-page">
        <div className="ob-wrap">
          <div className="ob-loading-card">
            <span className="ob-spinner" aria-label="Cargando" />
            <p className="ob-sub" style={{ marginTop: 16, marginBottom: 0 }}>Creando tu perfil…</p>
          </div>
        </div>
        <style>{CSS}</style>
      </div>
    );
  }

  if (!registrationEnabled) {
    return (
      <div className="ob-page">
        <div className="ob-wrap">
          <div className="ob-loading-card">
            <div className="ob-topbar-logo" style={{ marginBottom: 24 }}><ContactLogo /></div>
            <h1 className="ob-title" style={{ fontSize: 26, marginBottom: 10 }}>Acceso restringido</h1>
            <p className="ob-sub" style={{ marginBottom: 24 }}>El acceso a la plataforma se encuentra actualmente restringido.</p>
            <a href="mailto:ventas@contactnetwork.cl" className="ob-btn-primary" style={{ textDecoration: 'none', maxWidth: 'none' }}>
              Contáctanos
            </a>
          </div>
        </div>
        <style>{CSS}</style>
      </div>
    );
  }

  const showPreview = step >= 2;

  return (
    <div className="ob-page">
      <div className="ob-wrap">
        <header className="ob-topbar">
          <div className="ob-topbar-logo"><ContactLogo /></div>
          <div className="ob-stepper">
            <div className="ob-stepper-bars">
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <span key={i} className={`ob-stepper-bar ${i < step ? 'ob-stepper-bar--done' : ''}`} />
              ))}
            </div>
            <span className="ob-stepper-text">{step} de {TOTAL_STEPS}</span>
          </div>
        </header>

        <main className={`ob-main ${showPreview ? '' : 'ob-main--solo'}`}>
          <div className="ob-form-col">
            <div className={`ob-content ob-${contentPhase}`}>
              {renderStep()}
              {fieldError && <div className="ob-error" role="alert">{fieldError}</div>}
            </div>

            <div className="ob-nav-row">
              <button type="button" className="ob-btn-secondary" onClick={handleBack} disabled={transitioning}>
                <ArrowLeft size={15} /> Volver
              </button>
              <button
                type="button" className="ob-btn-primary"
                onClick={handlePrimary}
                disabled={transitioning || submitting}
              >
                {submitting ? <span className="ob-spinner ob-spinner--light" aria-label="Cargando" /> : primaryLabel()}
              </button>
            </div>
          </div>

          {showPreview && (
            <div className="ob-preview-col">
              <LivePreviewCard
                name={fullName} position={position} company={company}
                photoPreviewUrl={photoPreviewUrl} contactEmail={contactEmail} phone={phone}
                links={links} customLinks={customLinks}
              />
            </div>
          )}
        </main>
      </div>

      {linksModalOpen && (
        <LinksModal
          onClose={() => setLinksModalOpen(false)}
          onPick={handleModalPick}
          search={linksModalSearch}
          setSearch={setLinksModalSearch}
          category={linksModalCategory}
          setCategory={setLinksModalCategory}
          isFilled={(opt) => (opt.custom ? customLinks.some((l) => l.key === opt.key) : !!getLinkValue(opt))}
        />
      )}

      <style>{CSS}</style>
    </div>
  );
};

/* ─── CSS ────────────────────────────────────────────────── */
const CSS = `
@font-face {
  font-family: 'abcNormal';
  src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
:root{--ob-text:#0a0a0a;--ob-muted:#6b6b6b;--ob-faint:#a3a3a3;--ob-border:#e5e5e5;--ob-accent:#0071e3;--ob-accent-soft:#eaf3ff;--ob-bg:#fbfcfe}
*{box-sizing:border-box;margin:0;padding:0}

.ob-page{font-family:'Inter',system-ui,sans-serif;background:#fff;min-height:100dvh;color:var(--ob-text);overflow-x:hidden;position:relative;display:flex;flex-direction:column}

.ob-wrap{position:relative;z-index:2;flex:1;display:flex;flex-direction:column}
.ob-loading-card{max-width:340px;margin:auto;padding:56px 36px;display:flex;flex-direction:column;align-items:center;text-align:center}

.ob-topbar{display:flex;align-items:center;justify-content:space-between;padding:32px 40px 0}
.ob-topbar-logo{display:flex;align-items:center}
.ob-stepper{display:flex;align-items:center;gap:10px}
.ob-stepper-bars{display:flex;gap:5px}
.ob-stepper-bar{width:26px;height:4px;border-radius:3px;background:#e8e8e8;transition:background .2s}
.ob-stepper-bar--done{background:var(--ob-accent)}
.ob-stepper-text{font-size:12.5px;font-weight:600;color:var(--ob-muted)}

.ob-main{flex:1;display:flex;align-items:center;justify-content:center;gap:56px;padding:28px 40px 44px;width:100%}
.ob-main--solo{justify-content:center}

.ob-form-col{width:100%;max-width:440px;flex-shrink:0}

.ob-content{transition:opacity .21s cubic-bezier(.4,0,.2,1),transform .21s cubic-bezier(.4,0,.2,1)}
.ob-content.ob-out-fwd{opacity:0;transform:translateX(-22px)}
.ob-content.ob-out-back{opacity:0;transform:translateX(22px)}
.ob-content.ob-enter-fwd{opacity:0;transform:translateX(22px);transition:none}
.ob-content.ob-enter-back{opacity:0;transform:translateX(-22px);transition:none}
.ob-content.ob-resting{opacity:1;transform:translateX(0)}

.ob-title{font-family:'abcNormal','Inter','Arial',sans-serif;font-size:36px;font-weight:300;line-height:1.15;letter-spacing:-.02em;color:var(--ob-text);margin-bottom:8px}
.ob-sub{font-size:15px;color:var(--ob-muted);line-height:1.5;margin-bottom:28px;max-width:400px}

.ob-error{background:#fef2f2;border:1px solid #fecaca;color:#dc2626;padding:10px 14px;border-radius:10px;font-size:13px;line-height:1.5;margin-top:6px}

.ob-field{display:flex;flex-direction:column;gap:6px;margin-bottom:16px}
.ob-label{font-size:14px;font-weight:600;color:var(--ob-text)}
.ob-input{width:100%;height:52px;padding:0 16px;border:1.5px solid var(--ob-border);border-radius:14px;font-family:'Inter',system-ui,sans-serif;font-size:15.5px;color:var(--ob-text);background:#fff;outline:none;transition:border-color .15s,box-shadow .15s}
.ob-input::placeholder{color:var(--ob-faint)}
.ob-input:focus{border-color:var(--ob-accent);box-shadow:0 0 0 3px rgba(0,113,227,.12)}
.ob-input--pw{padding-right:48px}

.ob-pw-wrap{position:relative}
.ob-pw-toggle{position:absolute;right:0;top:0;width:48px;height:52px;display:flex;align-items:center;justify-content:center;background:none;border:none;color:var(--ob-faint);cursor:pointer;transition:color .15s}
.ob-pw-toggle:hover{color:var(--ob-muted)}

.ob-photo-wrap{display:flex;flex-direction:column;align-items:center;gap:18px;padding:8px 0 4px}
.ob-photo-circle{width:120px;height:120px;border-radius:50%;background:var(--ob-accent-soft);border:1px solid #dce9fb;display:flex;align-items:center;justify-content:center;color:var(--ob-accent);cursor:pointer;overflow:hidden;transition:border-color .15s;box-shadow:0 8px 24px rgba(0,113,227,.08)}
.ob-photo-circle:hover{border-color:#b9d7f9}
.ob-photo-img{width:100%;height:100%;object-fit:cover;border-radius:50%}
.ob-photo-initials{font-size:34px;font-weight:700;color:var(--ob-accent)}

.ob-btn-outline{height:44px;padding:0 22px;border-radius:100px;background:#fff;border:1.5px solid var(--ob-border);color:var(--ob-text);font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:background .15s,border-color .15s}
.ob-btn-outline:hover{background:#f7f8fa;border-color:#d0d0d0}

.ob-nav-row{display:flex;align-items:center;justify-content:space-between;gap:14px;margin-top:30px}
.ob-btn-secondary{display:inline-flex;align-items:center;gap:7px;height:52px;padding:0 24px;border-radius:100px;background:#fff;border:1.5px solid var(--ob-border);color:var(--ob-text);font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:background .15s,border-color .15s}
.ob-btn-secondary:hover:not(:disabled){background:#f7f8fa;border-color:#d0d0d0}
.ob-btn-secondary:disabled{opacity:.45;cursor:not-allowed}

.ob-btn-primary{flex:1;max-width:260px;height:52px;border-radius:100px;background:var(--ob-text);color:#fff;border:none;font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1),box-shadow .2s}
.ob-btn-primary:hover:not(:disabled){opacity:.9;transform:translateY(-1px);box-shadow:0 10px 24px rgba(0,0,0,.16)}
.ob-btn-primary:active:not(:disabled){transform:scale(.98)}
.ob-btn-primary:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none}

.ob-btn-google{width:100%;height:52px;border-radius:100px;background:#fff;border:1.5px solid var(--ob-border);color:var(--ob-text);font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:10px;transition:background .15s,border-color .15s}
.ob-btn-google:hover:not(:disabled){background:#f7f8fa;border-color:#d0d0d0}
.ob-btn-google:disabled{opacity:.5;cursor:not-allowed}
.ob-divider{position:relative;text-align:center;padding:4px 0 20px}
.ob-divider::before{content:'';position:absolute;top:50%;left:0;right:0;height:1px;background:var(--ob-border)}
.ob-divider span{position:relative;background:#fff;padding:0 12px;font-size:12px;color:var(--ob-faint)}

.ob-legal{font-size:12px;color:var(--ob-faint);line-height:1.5;margin-top:-4px}

@keyframes obSpin{to{transform:rotate(360deg)}}
.ob-spinner{width:20px;height:20px;border:2px solid rgba(0,113,227,.2);border-top-color:var(--ob-accent);border-radius:50%;animation:obSpin .55s linear infinite;flex-shrink:0}
.ob-spinner--light{border:2px solid rgba(255,255,255,.3);border-top-color:#fff}

/* ── LINKS: recommended rows + custom rows ── */
.ob-links-section{margin-top:6px;margin-bottom:18px}
.ob-links-section-title{font-size:12.5px;font-weight:700;color:var(--ob-muted);text-transform:uppercase;letter-spacing:.03em;margin-bottom:10px}
.ob-link-row{margin-bottom:8px}
.ob-link-card{width:100%;display:flex;align-items:center;gap:12px;height:52px;padding:0 14px;border-radius:16px;background:#fff;border:1px solid var(--ob-border);cursor:pointer;transition:background .15s,border-color .15s;text-align:left}
.ob-link-card:hover{background:#f7f8fa;border-color:#d8d8d8}
.ob-link-badge{display:flex;align-items:center;justify-content:center;flex-shrink:0}
.ob-link-card-label{flex:1;font-size:14px;font-weight:500;color:var(--ob-text)}
.ob-link-card-add{width:26px;height:26px;border-radius:50%;background:#f0f0f0;color:var(--ob-muted);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.ob-link-card-add--filled{background:rgba(22,163,74,.12);color:#16a34a}
.ob-link-input{margin-top:8px}
.ob-custom-link-head{display:flex;align-items:center;gap:10px;margin-bottom:6px}
.ob-custom-link-title{flex:1;font-size:13px;font-weight:600;color:var(--ob-text)}
.ob-custom-link-remove{width:22px;height:22px;border-radius:6px;background:#f0f0f0;border:none;color:var(--ob-muted);cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.ob-custom-link-remove:hover{background:#e5e5e5;color:var(--ob-text)}

.ob-add-links-btn{background:none;border:none;color:var(--ob-accent);font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;padding:4px 0}
.ob-add-links-btn:hover{opacity:.75}

/* ── LIVE PREVIEW (phone mock) ── */
.ob-preview-col{flex-shrink:0;display:flex;align-items:center;justify-content:center;animation:obPreviewIn .5s cubic-bezier(.22,1,.36,1) both}
@keyframes obPreviewIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
.ob-phone-mock{width:280px;background:#fff;border-radius:32px;overflow:hidden;box-shadow:0 16px 44px rgba(0,0,0,.14),0 4px 14px rgba(0,0,0,.07);border:1px solid #eee}
.ob-phone-banner{height:96px;background:linear-gradient(135deg,#0071e3,#3b9dff);position:relative}
.ob-phone-avatar{position:absolute;left:24px;bottom:-32px;width:76px;height:76px;border-radius:50%;background:#fff;border:4px solid #fff;box-shadow:0 6px 16px rgba(0,0,0,.14);display:flex;align-items:center;justify-content:center;color:var(--ob-accent);font-size:24px;font-weight:700;overflow:hidden}
.ob-phone-avatar img{width:100%;height:100%;object-fit:cover;border-radius:50%}
.ob-phone-body{padding:44px 22px 26px}
.ob-phone-name{font-size:18px;font-weight:700;color:var(--ob-text);letter-spacing:-.01em}
.ob-phone-role{font-size:13px;color:var(--ob-muted);margin-top:2px}
.ob-phone-save-btn{width:100%;height:42px;border-radius:14px;background:var(--ob-text);color:#fff;border:none;font-family:'Inter',system-ui,sans-serif;font-size:13.5px;font-weight:600;cursor:default;display:flex;align-items:center;justify-content:center;gap:7px;margin-top:16px}
.ob-phone-links{margin-top:16px;display:flex;flex-direction:column;gap:9px}
.ob-phone-link-row{display:flex;align-items:center;gap:9px;font-size:12.5px;color:var(--ob-muted);background:#f7f8fa;border-radius:10px;padding:9px 11px}
.ob-phone-link-row span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

/* ── LINKS MODAL ── */
.ob-modal-overlay{position:fixed;inset:0;z-index:300;background:rgba(10,10,10,.4);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:20px;animation:obModalFade .2s ease}
@keyframes obModalFade{from{opacity:0}to{opacity:1}}
@keyframes obModalSlide{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}

.ob-links-modal{position:relative;background:#fff;border-radius:22px;max-width:640px;width:100%;max-height:82vh;overflow-y:auto;padding:32px 34px;box-shadow:0 30px 80px rgba(0,0,0,.25);animation:obModalSlide .25s cubic-bezier(.16,1,.3,1)}
.ob-links-modal-close{position:absolute;top:24px;right:24px;width:32px;height:32px;border-radius:10px;background:#f5f5f5;border:none;color:var(--ob-muted);cursor:pointer;display:flex;align-items:center;justify-content:center}
.ob-links-modal-close:hover{background:#ececec;color:var(--ob-text)}
.ob-links-modal-head{margin-bottom:20px;padding-right:40px}
.ob-links-modal-title{font-size:22px;font-weight:700;color:var(--ob-text);letter-spacing:-.01em;margin-bottom:4px}
.ob-links-modal-sub{font-size:13.5px;color:var(--ob-muted)}
.ob-links-search{display:flex;align-items:center;gap:10px;height:48px;padding:0 16px;border-radius:16px;background:#f5f6f8;border:1px solid #eee;margin-bottom:16px;color:var(--ob-faint)}
.ob-links-search input{flex:1;border:none;background:none;outline:none;font-family:'Inter',system-ui,sans-serif;font-size:14.5px;color:var(--ob-text)}
.ob-links-chips{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:22px}
.ob-links-chip{font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;padding:8px 16px;border-radius:14px;border:1px solid var(--ob-border);background:#fff;color:var(--ob-muted);cursor:pointer;transition:background .15s,color .15s,border-color .15s}
.ob-links-chip--active{background:var(--ob-text);color:#fff;border-color:var(--ob-text)}
.ob-links-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.ob-links-grid-item{display:flex;align-items:center;gap:10px;height:56px;padding:0 12px;border-radius:16px;background:#fff;border:1px solid var(--ob-border);cursor:pointer;transition:background .15s,border-color .15s;text-align:left}
.ob-links-grid-item:hover{background:#f7f8fa;border-color:#d8d8d8}
.ob-links-grid-label{flex:1;font-size:13px;font-weight:500;color:var(--ob-text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ob-links-grid-add{width:24px;height:24px;border-radius:50%;background:#f0f0f0;color:var(--ob-muted);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.ob-links-grid-add--filled{background:rgba(22,163,74,.12);color:#16a34a}
.ob-links-empty{grid-column:1/-1;text-align:center;padding:30px 0;color:var(--ob-faint);font-size:13.5px}

@media(max-width:960px){
  .ob-main{flex-direction:column;gap:32px;padding:24px 24px 36px}
  .ob-topbar{padding:24px 24px 0}
  .ob-form-col{max-width:480px}
  .ob-preview-col{order:-1}
  .ob-phone-mock{width:240px}
}
@media(max-width:480px){
  .ob-topbar{padding:20px 20px 0}
  .ob-title{font-size:28px}
  .ob-sub{font-size:14px;margin-bottom:22px}
  .ob-input{height:52px;font-size:15px}
  .ob-pw-toggle{height:52px}
  .ob-nav-row{flex-wrap:wrap}
  .ob-btn-primary{max-width:none}
  .ob-links-grid{grid-template-columns:1fr 1fr}
  .ob-links-modal{padding:26px 22px;border-radius:26px}
}
@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default RegisterOnboardingPage;
